import React, { useState } from 'react'
import styled from 'styled-components'
import { Layout, Menu } from 'antd'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  SquareHalf,
  FilmSlate,
  UserRectangle,
  Users,
  Trophy,
  ChatTeardropText,
  Notification,
  Gear,
  SignOut,
} from 'phosphor-react'
import { Images } from 'assets'
import { useAuth } from 'context'

const { Sider } = Layout

const StyledLayout = styled(Layout)`
  max-width: 100vw;
  height: 100vh;
  overflow: hidden;
  flex-direction: row;

  .ant-layout-content {
    padding: 14px;
    background: #f7f7fa;
    border-radius: 8px;
    height: calc(100vh - 64px);
    overflow-y: auto;
    width: 100%;
  }

  @media (max-width: 768px) {
    .ant-layout-sider {
      display: none;
    }
  }
`

const SliderStyled = styled(Sider)`
  height: 100vh;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.white};
  overflow-y: scroll;
  scrollbar-width: none;

  ::-webkit-scrollbar {
    display: none;
  }

  .ant-menu {
    border-inline-end: 0 !important;
  }

  .ant-menu-item-active,
  .ant-menu-item-selected {
    // background: linear-gradient(90deg, #5a65c9 20%, #303b97 100%) !important;
    color: ${({ theme }) => theme.colors.primary} !important;

    .ant-menu-title-content {
      color: #fff !important;
    }

    svg {
      color: #fff !important;
    }

    &:hover {
      bacground: #fff;
      color: #5c67ca !important;
      font-weight: 400;

      svg {
        color: #fff !important;
      }
    }
  }

  box-shadow: 6.33px 6.33px 50.94px 0px #0000000d;
  flex: 0 0 240px !important;

  .ant-layout-sider-children {
    background: ${({ theme }) => theme.colors.white} !important;
    display: flex !important;
    justify-content: space-between;
    flex-direction: column;
  }
`
const MenuStyled = styled(Menu)`
  background: ${({ theme }) => theme.colors.white} !important;
  color: #1a1a1a;

  .ant-menu-item,
  .ant-menu-submenu-title {
    height: 50px !important;
    display: flex;
    align-items: center;
    gap: 12px;
    transition: all 0.3s ease;
    // background: transparent !important;
    color: #1a1a1a;

    .ant-menu-title-content {
      color: #1a1a1a !important;
    }

    svg {
      font-size: 24px !important;
      width: 24px !important;
      height: 24px !important;
      min-width: 24px !important;
      min-height: 24px !important;
      max-width: 24px !important;
      max-height: 24px !important;
      color: #1a1a1a;
      transition: color 0.3s ease-in-out;
    }

    &:hover {
      background: transparent !important;
      color: #5c67ca !important;

      .ant-menu-title-content {
        color: #5c67ca !important;
      }

      svg {
        color: #5c67ca !important;
      }
    }
  }

  .ant-menu-item-active,
  .ant-menu-item-selected {
    background: linear-gradient(90deg, #5a65c9 20%, #303b97 100%) !important;
    color: #ffffff !important;
    &:hover {
      background: #e4e6f9 !important;
      color: #5c67ca !important;
    }
    .ant-menu-title-content {
      color: #ffffff !important;
    }

    svg {
      color: #ffffff !important;
    }
  }
`

const LogoWrapper = styled.div`
  align-items: center;
  display: flex;
  flex-direction: row;
  padding: 10px;
  background: ${({ theme }) => theme.colors.white} !important;

  img {
    width: 100%;
    max-width: 50px;
    height: auto;
  }
  .ant-layout-sider-collapsed & {
    display: none;
  }
`

const RightLayout = styled(Layout)`
  height: 100vh;
  background: ${({ theme }) => theme.colors.white};
  border-inline-start: 1px solid rgba(5, 5, 5, 0.06);
`

const ScrollableContainer = styled.div`
  height: 100vh;
  overflow-y: scroll;
  scrollbar-width: thin;
  background: #fafafa;
  padding: 4px;
`

const SibarToggleButton = styled.div`
  cursor: pointer;
  position: absolute;
  top: 40%;
  z-index: 999;
`

const menuItems = [
  { key: 'dashboard', label: 'Dashboard', icon: <SquareHalf size={24} /> },
  { key: 'videos', label: 'Videos Page', icon: <FilmSlate size={24} /> },
  {
    key: 'manage-creators',
    label: 'Manage Creators',
    icon: <UserRectangle size={24} />,
  },
  { key: 'manage-viewers', label: 'Manage Viewers', icon: <Users size={24} /> },
  {
    key: 'competitions',
    label: 'Manage Competitions',
    icon: <Trophy size={24} />,
  },
  {
    key: 'flagged-comments',
    label: 'Flagged Comments',
    icon: <ChatTeardropText size={24} />,
  },
  {
    key: 'notifications',
    label: 'Notifications',
    icon: <Notification size={24} />,
  },
  {
    key: 'settings',
    label: 'Settings & Preferences',
    icon: <Gear size={24} />,
  },
]

const logoutItem = [
  {
    key: 'logout',
    label: 'Logout',
    icon: <SignOut size={24} />,
  },
]

export const AppLayout = () => {
  const { logout } = useAuth()
  const navigate = useNavigate()
  const [collapsed, setCollapsed] = useState(false)
  const location = useLocation()

  const selectedKey =
    menuItems.find(item => location.pathname.startsWith(`/app/${item.key}`))
      ?.key || 'dashboard'

  return (
    <StyledLayout>
      <SliderStyled collapsed={collapsed} collapsedWidth={80} width={240}>
        <div>
          <LogoWrapper>
            <h4 className="mb-0 ms-3">Oriri Admin</h4>
          </LogoWrapper>
          <MenuStyled
            mode="inline"
            inlineCollapsed={collapsed}
            defaultSelectedKeys={['dashboard']}
            selectedKeys={[selectedKey]}
            items={menuItems}
            onClick={item => {
              navigate(`/app/${item.key}`)
            }}
          />
        </div>
        <MenuStyled
          mode="inline"
          inlineCollapsed={collapsed}
          items={logoutItem}
          onSelect={item => {
            if (item.key === 'logout') {
              logout()
            }
          }}
        />
      </SliderStyled>

      <RightLayout>
        <SibarToggleButton onClick={() => setCollapsed(!collapsed)}>
          <img
            className="sidebae"
            src={!collapsed ? Images.SideBarCloseIcon : Images.SideBarOpenIcon}
            alt="Toggle"
          />
        </SibarToggleButton>
        <ScrollableContainer>
          <Outlet />
        </ScrollableContainer>
      </RightLayout>
    </StyledLayout>
  )
}
